import React, { useEffect, useState } from 'react'

import { Box, Button, Grid, Typography, Paper } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams } from 'react-router-dom'

import { useQueryState } from '~/common/hooks'
import ActionBar from '~/components/ActionBar'
import LV from '~/components/LabelValue'
import Page from '~/components/Page'
import Status from '~/components/Status'
import { GUEST_CONTACT_STATUS_OPTIONS } from '~/modules/configuration/constants'
import useGuestContact from '~/modules/shared/redux/hooks/useGuestContact'
import { format, differenceInDays } from 'date-fns'
import { enUS, ja, vi } from 'date-fns/locale'

import { ROUTE } from '../../routes/config'
import AddNoteDialog from './dialogs/add-note'
import CreateAccountDialog from './dialogs/create-account'
import Notes from './notes'

const formatDate = (dateTime, formatPattern) => {
  const currFormat = formatPattern || 'dd/MM/yyyy HH:mm:ss'

  return dateTime
    ? format(new Date(dateTime), currFormat)
    : ''
}

const GuestDetail = () => {
  const history = useHistory()
  const { t } = useTranslation(['mkt'])
  const { withSearch } = useQueryState()

  const [isOpenAddNoteModal, setIsOpenAddNoteModal] = useState(false)
  const [isOpenCreateAccountModal, setIsOpenCreateAccountModal] =
    useState(false)

  const {
    data: { isLoading, guestContactDetail },
    actions,
  } = useGuestContact()

  const { id } = useParams()
  const isRegisted = guestContactDetail.status === 1

  const refreshData = () => actions.getGuestContactDetailsById(id)
  useEffect(() => {
    refreshData()
    return () => {
      actions.resetGuestContactDetailState()
    }
  }, [id])

  const breadcrumbs = [
    {
      route: withSearch(ROUTE.GUEST.LIST.PATH),
      title: ROUTE.GUEST.LIST.TITLE,
    },
    {
      title: ROUTE.GUEST.DETAIL.TITLE,
    },
  ]

  const backToList = () => {
    history.push(withSearch(ROUTE.GUEST.LIST.PATH))
  }

  const convertTransaleValues = (value) => {
    if (!value) return ''
    const keys = value?.split(',')
    const values = keys?.map((key) => t(key))

    return values?.map((v) => (
      <Typography
        component="span"
        sx={(theme) => ({
          display: 'inline-block',
          marginRight: 2,
          marginBottom: 1,
          borderRadius: 1,
          p: '0 8px',
          backgroundColor:
            theme.palette.status.created.background ||
            theme.palette.status.created.background,
          color: `status.created.contrastText`,
          fontWeight: 500,
        })}
      >
        {v}
      </Typography>
    ))
  }

  const getNotes = () => {
    const notes = guestContactDetail?.notes
    return notes
  }

  const onSubmitAddNote = (values) => {
    const params = {
      id: id,
      note: values?.note,
    }
    actions.addNoteGuestContact(params, () => {
      refreshData()
    })
    setIsOpenAddNoteModal(false)
  }

  const onSubmitCreateAccount = (values) => {
    const params = {
      id: id,
      username: values?.username,
      password: values?.password,
      modules: values?.modules,
    }
    actions.createAccountGuestContact(
      params,
      (data) => {
        setIsOpenCreateAccountModal(false)
        refreshData()
      },
      (err) => {
        console.log(err)
      },
    )
  }

  return (
    <Page
      breadcrumbs={breadcrumbs}
      title={t('menu.guestDetail')}
      loading={isLoading}
      onBack={backToList}
      freeSolo
    >
      <Paper sx={{ p: 1 }}>
        <Grid container justifyContent="center">
          <Grid item xl={11} xs={12}>
            <Grid container rowSpacing={1} columnSpacing={{ xl: 8, xs: 4 }}>
              <Grid item lg={6} xs={12}>
                <LV
                  label={t('guest.status')}
                  value={
                    <Status
                      options={GUEST_CONTACT_STATUS_OPTIONS}
                      value={guestContactDetail?.status}
                    />
                  }
                />
              </Grid>
              {guestContactDetail.mesxUsername && (
                <Grid item lg={6} xs={12}>
                  <LV
                    label={t('guest.mesxUsername')}
                    value={guestContactDetail.mesxUsername}
                  />
                </Grid>
              )}
              <Grid item lg={6} xs={12}>
                <LV
                  label={t('guest.fullName')}
                  value={guestContactDetail?.fullname}
                />
              </Grid>
              <Grid item lg={6} xs={12}>
                <LV
                  label={t('guest.phoneNumber')}
                  value={guestContactDetail?.phone}
                />
              </Grid>
              <Grid item lg={6} xs={12}>
                <LV
                  label={t('guest.workEmail')}
                  value={guestContactDetail?.email}
                />
              </Grid>
              <Grid item lg={6} xs={12}>
                <LV
                  label={t('guest.companyName')}
                  value={guestContactDetail?.companyName}
                />
              </Grid>
              <Grid item lg={6} xs={12}>
                <LV
                  label={t('guest.area')}
                  value={guestContactDetail?.addressZone}
                />
              </Grid>
              <Grid item lg={6} xs={12}>
                <LV
                  label={t('guest.currentRole')}
                  value={convertTransaleValues(guestContactDetail?.role)}
                />
              </Grid>
              <Grid item lg={6} xs={12}>
                <LV
                  label={t('guest.interestFeature')}
                  value={convertTransaleValues(guestContactDetail?.features)}
                />
              </Grid>{' '}
              <Grid item lg={6} xs={12}>
                <LV
                  label={t('guest.workType')}
                  value={convertTransaleValues(
                    guestContactDetail?.bussinessType,
                  )}
                />
              </Grid>
              <Grid item lg={6} xs={12}>
                <LV
                  label={t('guest.experientalPurpose')}
                  value={convertTransaleValues(guestContactDetail?.purpose)}
                />
              </Grid>{' '}
              <Grid item lg={6} xs={12}>
                <LV label={t('guest.createdAt')} value={guestContactDetail.registedAt} />
              </Grid>
                <Grid item lg={6} xs={12}>
                <LV label={'IP'} value={guestContactDetail.ip} />
              </Grid>
            </Grid>
            <ActionBar onBack={backToList} />
          </Grid>
        </Grid>
      </Paper>
      <Paper sx={{ p: 1, mt: 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            flexWrap: 'wrap',
            pb: 1,
            gap: 1,
          }}
        >
          <Button
            variant="outlined"
            color="success"
            onClick={() => setIsOpenAddNoteModal(true)}
          >
            {t('guest.addNote')}
          </Button>
          {isRegisted && (
            <Button
              variant="outlined"
              color="warning"
              onClick={() => setIsOpenCreateAccountModal(true)}
            >
              {t('guest.createAccount')}
            </Button>
          )}
        </Box>
      </Paper>
      <Notes data={getNotes()} title={t('guest.notes')} />
      <AddNoteDialog
        isOpenAddNoteModal={isOpenAddNoteModal}
        setIsOpenAddNoteModal={setIsOpenAddNoteModal}
        onSubmit={onSubmitAddNote}
      />
      <CreateAccountDialog
        isOpenCreateAccountModal={isOpenCreateAccountModal}
        setIsOpenCreateAccountModal={setIsOpenCreateAccountModal}
        onSubmit={onSubmitCreateAccount}
      />
    </Page>
  )
}

export default GuestDetail
